'use client';

import { Language } from '@order-mage/integration-sdk';
import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, memo } from 'react';
import {
    CartFormValues,
    buildPackageItemByIdMap,
    getAddonPillLabel,
    getAddonPills,
    resolveEffectiveAddons,
    selectedAddonLines,
    type AddonPill,
} from '@/entities/cart';
import type { CartMenuItem } from '@/entities/cart/lib/item-price';
import type { ResolvedStorefrontItem } from '@/entities/product/lib/storefront-menu';
import { useStorefrontCartMenuData } from '@/entities/product/lib/useStorefrontMenu';
import { ProductDetailModal } from '@/entities/product/ui/product-detail/product-detail-v2';
import { useGetPageUrl } from '@/schema/useGetPageUrl';
import { Link } from '@/shared/i18n/navigation';
import { cn } from '@/shared/lib/cn';
import { EventService } from '@/shared/lib/event-service';
import { getMainAsset } from '@/shared/lib/getMainAsset';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';
import { AmountInput } from '@/shared/ui/amount-input';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Icon } from '@/shared/ui/icon';
import { Media } from '@/shared/ui/media';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import { CartItemPackageLines, CartItemPriceWithPackages } from './cart-item-packages-and-price';

const EditButton = ({
    data,
    className,
    ...props
}: {
    data: Omit<ComponentProps<typeof ProductDetailModal>, keyof ModalProps>;
} & ComponentProps<typeof Button>) => {
    return (
        <Button
            variant="secondary"
            className={cn('cart-items-list-summary-box__edit-button', className)}
            data-testid="cart-item-edit-button"
            onClick={() => {
                ModalService.showPrefab('ProductDetailModal', data);
            }}
            {...props}
        >
            <Icon name="edit" width={24} height={24} />
        </Button>
    );
};

type PackageItemById = ReturnType<typeof buildPackageItemByIdMap>;
type PackageSource = Pick<CartMenuItem, 'packages' | 'packageBehaviours'>;

type CartItemRowProps = {
    /** Field-array row id (`_id`) — used for cart events. */
    id: string;
    hash: string;
    quantity: number;
    variant: string | undefined;
    addons: CartFormValues['items'][number]['addons'];
    memberVariants: CartFormValues['items'][number]['memberVariants'];
    note: string | undefined;
    itemsLookup: Record<string, ResolvedStorefrontItem>;
    idToHash: Map<string, string>;
    packageItemById: PackageItemById;
    selectedCountry: Country;
    selectedCurrency: Currency;
    userLanguage: Language;
    detailAsModal: boolean;
};

// RHF rebuilds every `fields[i]` object on any update — compare values, not field refs.
const areCartItemRowPropsEqual = (prev: CartItemRowProps, next: CartItemRowProps) =>
    prev.id === next.id &&
    prev.hash === next.hash &&
    prev.quantity === next.quantity &&
    prev.variant === next.variant &&
    prev.note === next.note &&
    prev.addons === next.addons &&
    prev.memberVariants === next.memberVariants &&
    prev.itemsLookup === next.itemsLookup &&
    prev.idToHash === next.idToHash &&
    prev.packageItemById === next.packageItemById &&
    prev.selectedCountry === next.selectedCountry &&
    prev.selectedCurrency === next.selectedCurrency &&
    prev.userLanguage === next.userLanguage &&
    prev.detailAsModal === next.detailAsModal;

export const CartItemRow = memo(function CartItemRow({
    id: _id,
    hash: fieldHash,
    quantity,
    variant: cartVariant,
    addons: cartAddons,
    memberVariants,
    note,
    itemsLookup,
    packageItemById,
    selectedCountry,
    selectedCurrency,
    userLanguage,
    detailAsModal,
}: CartItemRowProps) {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const getPageUrl = useGetPageUrl();

    const cartMenuData = useStorefrontCartMenuData();

    const item = itemsLookup[fieldHash];
    if (!item) return null;

    const { id, name, addons, packageBehaviours } = item.body;
    const hash = item.hash;
    const slug = getLocalizedValue(item.placement.slug);

    const variantEntry = cartVariant && cartVariant !== fieldHash ? itemsLookup[cartVariant] : undefined;
    const variantBody =
        variantEntry && variantEntry.placement.type === 'variant' ? variantEntry.body : undefined;
    const assets = variantBody?.assets?.length ? variantBody.assets : item.body.assets;

    const effectiveAddons = resolveEffectiveAddons(itemsLookup, addons, cartVariant, hash);
    const lines =
        effectiveAddons?.length && cartAddons
            ? selectedAddonLines(effectiveAddons, cartAddons, itemsLookup, memberVariants)
            : [];

    const { added: addedPills, removed: removedPills } = getAddonPills(lines);
    const renderPills = (pills: AddonPill[], removed: boolean): ReactNode =>
        pills.map((pill, index) => {
            return (
                <span
                    key={`${removed ? 'removed' : 'added'}:${pill.itemId ?? 'mod'}:${index}`}
                    className={cn(
                        'cart-items-list-summary-box__mod-pill',
                        removed && 'cart-items-list-summary-box__mod-pill--removed',
                    )}
                    data-testid="cart-item-modifications-row"
                    data-type={removed ? 'removed' : 'added'}
                >
                    <span>{removed ? '-' : '+'}</span>
                    <span
                        data-testid={removed ? 'cart-item-modification-removed' : 'cart-item-modification'}
                        data-item-id={pill.itemId}
                        data-amount={pill.count}
                    >
                        {getAddonPillLabel(pill, getLocalizedValue)}
                    </span>
                </span>
            );
        });

    const localizedName = getLocalizedValue(name);
    const mainAsset = getMainAsset(assets);

    const parentCartItem = cartMenuData?.items[fieldHash]?.item;
    const variantCartItem = cartVariant ? cartMenuData?.items[cartVariant]?.item : undefined;
    const isDefaultVariantSelected =
        variantEntry?.placement.type === 'variant' && variantEntry.placement.isDefault === true;
    // ponytail: cheap derive; useMemo after early `if (!item)` would break rules-of-hooks
    const packagesSourceItem: PackageSource =
        variantCartItem && !isDefaultVariantSelected
            ? variantCartItem
            : {
                  packages: parentCartItem?.packages,
                  packageBehaviours: variantCartItem?.packageBehaviours ?? packageBehaviours,
              };

    const variant = cartVariant && cartVariant !== hash ? itemsLookup[cartVariant] : undefined;
    const defaultVariantHash = parentCartItem?.variants?.find((v) => v.isDefault)?.hash;
    const variantName = variant
        ? getLocalizedValue(variant.body.name)
        : defaultVariantHash
          ? getLocalizedValue(cartMenuData?.items[defaultVariantHash]?.item?.name ?? name)
          : undefined;

    const data = {
        id: _id,
        hash,
        initialQuantity: quantity,
        initialVariant: cartVariant,
        initialAddons: cartAddons,
        initialMemberVariants: memberVariants,
        initialNote: note,
    };

    return (
        <Card
            className="cart-items-list-summary-box__item"
            data-testid="cart-items-list-summary-box-item"
            data-id={id}
            data-variant-id={variant?.body?.id}
        >
            <div className="cart-items-list-summary-box__content">
                {mainAsset && (
                    <div className="cart-items-list-summary-box__image-wrapper">
                        <Media
                            data={{ url: mainAsset.fullUrl ?? '', alt: `${localizedName}-image` }}
                            width={128}
                            height={128}
                            className="cart-items-list-summary-box__image"
                            data-testid="cart-item-image"
                        />
                        <EditButton data={data} />
                    </div>
                )}
                <div className="cart-items-list-summary-box__info">
                    <div className="cart-items-list-summary-box__header">
                        <div className="cart-items-list-summary-box__title-row">
                            <div className="cart-items-list-summary-box__title-wrapper">
                                {detailAsModal ? (
                                    <button
                                        type="button"
                                        className="cart-items-list-summary-box__title"
                                        data-testid="cart-item-title"
                                        onClick={() => {
                                            EventService.trigger('product:addons-menu:view-detail', {
                                                productHash: hash,
                                                initialVariant: cartVariant,
                                            });
                                        }}
                                    >
                                        {localizedName}
                                    </button>
                                ) : (
                                    <Link
                                        href={getPageUrl('product-detail', {
                                            PRODUCT_SLUG: `${slug}-${hash}`,
                                        })!}
                                        className="cart-items-list-summary-box__title"
                                        data-testid="cart-item-title"
                                    >
                                        {localizedName}
                                    </Link>
                                )}
                                {!mainAsset && <EditButton variant="ghost" data={data} />}
                            </div>
                            <Button
                                variant="ghost"
                                className="cart-items-list-summary-box__delete-button"
                                data-testid="cart-item-delete-button"
                                onClick={() => {
                                    EventService.trigger('cart:product:remove-from-cart', {
                                        id: _id,
                                    });
                                }}
                            >
                                <Icon name="trash" width={24} height={24} />
                            </Button>
                        </div>
                        <CartItemPackageLines
                            packagesSourceItem={packagesSourceItem}
                            cartAddons={cartAddons}
                            itemAddons={effectiveAddons ?? addons}
                            memberVariants={memberVariants}
                            packageItemById={packageItemById}
                            selectedCountry={selectedCountry}
                            selectedCurrency={selectedCurrency}
                            userLanguage={userLanguage}
                        />
                        {variantName && (
                            <span
                                className="cart-items-list-summary-box__meta"
                                data-testid="cart-item-variant"
                            >
                                {variantName}
                            </span>
                        )}
                    </div>
                    <div className="cart-items-list-summary-box__footer">
                        <CartItemPriceWithPackages
                            packagesSourceItem={packagesSourceItem}
                            cartAddons={cartAddons}
                            packageItemById={packageItemById}
                            selectedCountry={selectedCountry}
                            selectedCurrency={selectedCurrency}
                            userLanguage={userLanguage}
                            hash={hash}
                            cartVariant={cartVariant}
                            itemAddons={effectiveAddons ?? addons}
                            memberVariants={memberVariants}
                        />
                        <AmountInput
                            data-testid="cart-item-amount-input"
                            value={quantity}
                            plusButtonProps={{
                                onClick: () => {
                                    EventService.trigger('cart:product:change-quantity', {
                                        id: _id,
                                        quantity: quantity + 1,
                                    });
                                },
                            }}
                            minusButtonProps={{
                                onClick: () => {
                                    if (quantity - 1 <= 0) {
                                        EventService.trigger('cart:product:remove-from-cart', {
                                            id: _id,
                                        });
                                    }
                                    EventService.trigger('cart:product:change-quantity', {
                                        id: _id,
                                        quantity: quantity - 1,
                                    });
                                },
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="cart-items-list-summary-box__modifications">
                {addedPills.length + removedPills.length > 0 && (
                    <span className="cart-items-list-summary-box__mod-label">{t('WORDS.MODIFICATIONS')}</span>
                )}
                {renderPills(addedPills, false)}
                {renderPills(removedPills, true)}
                <b className="cart-items-list-summary-box__note" data-testid="cart-item-note">
                    {note}
                </b>
            </div>
        </Card>
    );
}, areCartItemRowPropsEqual);
